// src/components/modals/ViewSalaryModal.jsx
import React from 'react'
import { X, User, Calendar, DollarSign, TrendingUp, TrendingDown, Building, Phone, Mail, CreditCard, Calculator, FileText, Clock } from 'lucide-react'

const ViewSalaryModal = ({ isOpen, onClose, salary }) => {
  // Don't render if not open or no salary
  if (!isOpen || !salary) return null

  // Format currency
  const formatCurrency = (amount) => {
    return `Rs ${parseFloat(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`
  }

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    } catch {
      return 'Invalid date'
    }
  }

  // Format month for display
  const formatMonth = (dateString) => {
    if (!dateString) return 'N/A'
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long'
      })
    } catch {
      return 'Invalid date'
    }
  }

  // Calculate totals
  const totalAllowances = Object.values(salary.allowances || {}).reduce((sum, val) => sum + parseFloat(val || 0), 0)
  const totalDeductions = Object.values(salary.deductions || {}).reduce((sum, val) => sum + parseFloat(val || 0), 0)

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <FileText className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Payslip Details</h3>
                <p className="text-sm text-gray-600">{formatMonth(salary.month)}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 max-h-[calc(90vh-120px)] overflow-y-auto space-y-4">
            {/* Employee Information */}
            <div>
              <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                <User className="h-4 w-4 mr-2" />
                Employee Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
                  <div className="text-sm font-semibold text-gray-900">
                    {salary.employee?.fullName || 'N/A'}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Employee Type</label>
                  <div className="text-sm text-gray-900 capitalize">
                    {salary.employee?.employeeType || 'N/A'}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Contact</label>
                  <div className="text-sm text-gray-900 flex items-center space-x-1">
                    <Phone className="h-3 w-3" />
                    <span>{salary.employee?.contactNumber || 'N/A'}</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Email</label>
                  <div className="text-sm text-gray-900 flex items-center space-x-1">
                    <Mail className="h-3 w-3" />
                    <span>{salary.employee?.emailAddress || 'N/A'}</span>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">NIC/Passport/DL</label>
                  <div className="text-sm text-gray-900">
                    {salary.employee?.nicPassportDrivingLicense || 'N/A'}
                  </div>
                </div>

                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Date of Joining</label>
                  <div className="text-sm text-gray-900 flex items-center space-x-1">
                    <Calendar className="h-3 w-3" />
                    <span>{formatDate(salary.employee?.dateOfJoining)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Bank Information */}
            {salary.employee?.bankAccount && (
              <div>
                <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                  <CreditCard className="h-4 w-4 mr-2" />
                  Bank Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="bg-blue-50 rounded-lg p-3">
                    <label className="block text-xs font-medium text-blue-600 mb-1">Account Name</label>
                    <div className="text-sm font-medium text-blue-900">
                      {salary.employee.bankAccount.accountName || 'N/A'}
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3">
                    <label className="block text-xs font-medium text-blue-600 mb-1">Account Number</label>
                    <div className="text-sm font-mono text-blue-900">
                      {salary.employee.bankAccount.accountNumber || 'N/A'}
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3">
                    <label className="block text-xs font-medium text-blue-600 mb-1">Bank Name</label>
                    <div className="text-sm text-blue-900 flex items-center space-x-1">
                      <Building className="h-3 w-3" />
                      <span>{salary.employee.bankAccount.bankName || 'N/A'}</span>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-3">
                    <label className="block text-xs font-medium text-blue-600 mb-1">Branch</label>
                    <div className="text-sm text-blue-900">
                      {salary.employee.bankAccount.bankBranch || 'N/A'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Salary Details */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {/* Left Column - Basic & Allowances */}
              <div className="space-y-4">
                {/* Basic Salary Information */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <DollarSign className="h-4 w-4 mr-2" />
                    Basic Information
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <label className="block text-xs font-medium text-gray-600 mb-1">Basic Salary</label>
                      <div className="text-lg font-bold text-gray-900">
                        {formatCurrency(salary.basicSalary)}
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div className="bg-orange-50 rounded-lg p-3">
                        <label className="block text-xs font-medium text-orange-600 mb-1">
                          <Clock className="h-3 w-3 inline mr-1" />
                          Overtime Hours
                        </label>
                        <div className="text-sm font-semibold text-orange-900">
                          {salary.overtimeDurationInHours || 0} hrs
                        </div>
                      </div>

                      <div className="bg-red-50 rounded-lg p-3">
                        <label className="block text-xs font-medium text-red-600 mb-1">Extra Leaves</label>
                        <div className="text-sm font-semibold text-red-900">
                          {salary.extraLeaves || 0} days
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Allowances */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <TrendingUp className="h-4 w-4 mr-2 text-green-600" />
                    Allowances
                  </h4>
                  <div className="bg-green-50 rounded-lg p-3 border border-green-200">
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      {Object.entries(salary.allowances || {}).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-green-700 capitalize">{key}:</span>
                          <span className="font-medium text-green-900">
                            {formatCurrency(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-green-300">
                      <div className="flex justify-between font-semibold">
                        <span className="text-green-700">Total Allowances:</span>
                        <span className="text-green-900">{formatCurrency(totalAllowances)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column - Deductions & Summary */}
              <div className="space-y-4">
                {/* Deductions */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <TrendingDown className="h-4 w-4 mr-2 text-red-600" />
                    Deductions
                  </h4>
                  <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                    <div className="grid grid-cols-1 gap-2 text-sm">
                      {Object.entries(salary.deductions || {}).map(([key, value]) => (
                        <div key={key} className="flex justify-between">
                          <span className="text-red-700 capitalize">
                            {key.replace(/([A-Z])/g, ' $1').trim()}:
                          </span>
                          <span className="font-medium text-red-900">
                            {formatCurrency(value)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 pt-2 border-t border-red-300">
                      <div className="flex justify-between font-semibold">
                        <span className="text-red-700">Total Deductions:</span>
                        <span className="text-red-900">{formatCurrency(totalDeductions)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Company Contributions */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3">Company Contributions</h4>
                  <div className="space-y-2">
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-purple-700 font-medium">EPF Employer (12%):</span>
                        <span className="text-purple-900 font-semibold">
                          {formatCurrency(salary.epfEmployer)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="bg-purple-50 rounded-lg p-3 border border-purple-200">
                      <div className="flex justify-between text-sm">
                        <span className="text-purple-700 font-medium">ETF Employer (3%):</span>
                        <span className="text-purple-900 font-semibold">
                          {formatCurrency(salary.etfEmployer)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Salary Summary */}
                <div>
                  <h4 className="text-base font-semibold text-gray-900 mb-3 flex items-center">
                    <Calculator className="h-4 w-4 mr-2" />
                    Salary Summary
                  </h4>
                  <div className="space-y-3">
                    <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-blue-700">Gross Salary:</span>
                        <span className="text-lg font-bold text-blue-900">
                          {formatCurrency(salary.grossSalary)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="bg-green-50 rounded-lg p-4 border-2 border-green-300">
                      <div className="flex justify-between items-center">
                        <span className="text-base font-semibold text-green-700">Net Payable Salary:</span>
                        <span className="text-2xl font-bold text-green-900">
                          {formatCurrency(salary.netSalary)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Metadata */}
            <div className="border-t pt-3">
              <h4 className="text-base font-semibold text-gray-900 mb-3">Record Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Created On</label>
                  <p className="text-sm text-gray-900">
                    {formatDate(salary.createdAt)}
                    {salary.createdBy && (
                      <span className="text-gray-500 block text-xs">
                        by {salary.createdBy.username || 'Unknown User'}
                      </span>
                    )}
                  </p>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-3">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Last Updated</label>
                  <p className="text-sm text-gray-900">
                    {formatDate(salary.updatedAt)}
                    {salary.updatedBy && (
                      <span className="text-gray-500 block text-xs">
                        by {salary.updatedBy.username || 'Unknown User'}
                      </span>
                    )}
                  </p>
                </div>
              </div>

              {/* Salary ID */}
              <div className="mt-3 bg-gray-50 rounded-lg p-2">
                <p className="text-xs text-gray-500">
                  <span className="font-medium">Salary ID:</span> {salary._id}
                </p>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end p-4 pt-0">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ViewSalaryModal